import { ZETRIX_RESPONSE_MESSAGE } from "./requestEnum";
import {
  mainChainId,
  chainIdMessage,
  testChainIdMessage,
  switchAccountIdMessage,
} from "./dictionary";
import { mapMutations } from "vuex";
import ZetrixWalletConnect from "zetrix-connect-wallet-sdk";
import { isMobile, isChrome } from "./util";
import { eventBus } from '@/utils/eventBus';

const _NON_PROD_INDICATORS = ["localhost", "test", "dev", "sandbox", "cloudflare"];
const _isNonProdUrl = (url) => _NON_PROD_INDICATORS.some((s) => (url || "").includes(s));
// _isTestnet is evaluated at build time from VUE_APP_TESTNET (explicit override) or the API URL keyword
// check — whichever fires first. No window.location access at module scope.
// isMainNetwork() additionally checks window.location.host at runtime — that runtime check is intentionally
// separate and does not affect the bridge/deep-link selection here.
const _isTestnet = process.env.VUE_APP_TESTNET === 'true' || _isNonProdUrl(process.env.VUE_APP_API_BASE_URL);
const BRIDGE_URL = _isTestnet
  ? "wss://test-wscw.zetrix.com"
  : (process.env.VUE_APP_WALLET_BRIDGE_URL || "wss://wscw.zetrix.com");
const _walletConnectOpts = (qrcode) => ({ bridge: BRIDGE_URL, qrcode, testnet: _isTestnet, appType: "zetrixwv3" });

export default {
  data() {
    return {
      ztx: window.zetrix,
      isMobile: false,
      isChrome: false,
      zetrixWalletConnect: new ZetrixWalletConnect(_walletConnectOpts(false)),
      zetrixAccount: sessionStorage.getItem("zetrixAccount"),
      balance: this.$store.state.balance,
      isLogin: sessionStorage.getItem("isLogin"),
      errorNetwork: "",
      mobileStep: "idle", // 'idle' | 'connecting' | 'ready' | 'waiting' | 'sign-ready' | 'signing'
      pendingSign: null,  // { address, blob } after auth succeeds
    };
  },

  created() {
    // Timer IDs are non-reactive — initialized here to avoid Vue proxy warnings
    // for underscore-prefixed properties declared inside data().
    this._authTimeout = null;
    this._signTimeout = null;
  },

  watch: {
    ztx: {
      deep: true,
      handler: function () {
        this.ztx.on("chainChanged", (res) => {
          if (res.code === 0) {
            this.setChainId(res.data.chainId);
            if (!this.isMainNetwork()) {
              this.disconnectWalletZtx();
            }
          }
        });
        this.ztx.on("switchAccount", (res) => {
          if (res.code === 0) {
            this.zetrixAccount = res.data.address;
            if (
              this.zetrixAccount !== sessionStorage.getItem("zetrixAccount")
            ) {
              eventBus.triggerSessionTimeout();
            }
          }
        });
      },
    },
  },

  mounted() {
    this.isMobile = isMobile();
    this.isChrome = isChrome();
    this.checkBalanceZTX();
  },

  updated() {
    this.checkWindowZetrix();
  },

  beforeUnmount() {
    this.cancelMobileConnect();
  },

  methods: {
    ...mapMutations([
      "setLogin",
      "setHeaderMessage",
      "setChainId",
      "setBalance",
    ]),

    checkWindowZetrix() {
      if (typeof window.zetrix !== "undefined") {
        this.ztx = window.zetrix;
      }
    },

    checkBalanceZTX() {
      if (this.zetrixAccount !== null) {
        //this.getAccountBalance(this.zetrixAccount);
      }
    },

    // 检查zetrix是否安装
    checkZetrixInstall() {
      if (this.isMobile) {
        this.prepareMobileConnect();
      } else if (this.isChrome) {
        if (typeof window.zetrix === "undefined") {
          this.$store.dispatch("triggerSnackbar", "Zetrix Wallet extension not detected. Opening the Chrome Web Store to install it.");
          window.open("https://chromewebstore.google.com/detail/zetrix-wallet/lpjcijieclbbmdckfhomhfdnlfjelfmi");
        } else {
          this.ztx = window.zetrix;
          this.ztx.getChainId((res) => {
            if (res.code === 0) {
              this.setChainId(res.data.chainId);
              this.sendClientAuthorize();
            }
          });
        }
      } else {
        this.connectDesktopQr();
      }
    },

    connectDesktopQr() {
      try { this.zetrixWalletConnect?.closeConnect(); } catch { }
      this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(true));
      this.connectWalletQrCode();
    },

    sendClientAuthorize() {
      if (!this.isMainNetwork()) {
        this.$store.dispatch("triggerSnackbar", this.errorNetwork);
        return false;
      }
      this.ztx.authorize({ method: "changeAccounts" }, async (resp) => {
        if (resp.code === 0) {
          this.authBlob(resp.data.address).then((blob) => {
            if (!blob) {
              this.$store.dispatch("triggerSnackbar", "Failed to get authentication data. Please try again.");
              return;
            }
            const blobHex = Array.from(new TextEncoder().encode(blob))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('');

            this.ztx.authorize(
              { method: "sendRandom", param: { random: blobHex } },
              async (resAuth) => {
                if (resAuth.code === 0) {

                  // Step 3: Call authLogin
                  this.authLogin(
                    resp.data.address,
                    resAuth.data.signData, // signMessage
                    resAuth.data.publicKey
                  ).then(async (accessToken) => {
                    if (accessToken) {
                      sessionStorage.setItem("zetrixAccount", resp.data.address);
                      sessionStorage.setItem("publicKey", resAuth.data.publicKey);

                      window.dispatchEvent(new Event("account-connected"));

                      this.zetrixAccount = resp.data.address;
                      //this.getAccountBalance(this.zetrixAccount);
                      this.isLogin = "true";
                      sessionStorage.setItem("isLogin", this.isLogin);
                      // ✅ Step 4: Check KYC status

                      if (sessionStorage.getItem("userRole") === "ISSUER") {
                        this.statusKyc().then(async (status) => {
                          if (!status) {
                            sessionStorage.setItem("kyc", status);
                            this.$router.push("/kyc-application");
                            return;
                          }

                          switch (status) {
                            case "PENDING":
                            case "REJECTED":
                            case "REVOKED":
                              sessionStorage.setItem("kyc", status);
                              this.$router.push("/kyc-status");
                              break;
                            case "ACTIVE":
                              // Fetch KYC details + user details concurrently
                              const [kycDetails, userInfo] = await Promise.all([
                                this.statusDetails({ address: this.zetrixAccount }),
                                this.userDetails(this.zetrixAccount)
                              ]);

                              sessionStorage.setItem("kyc", status);

                              if (userInfo?.object?.name) {
                                sessionStorage.setItem("userName", userInfo.object.name);
                              }
                              if (kycDetails?.object?.user?.name) {
                                sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
                              }

                              this.$router.push("/certificate");
                              break;
                            default:
                              sessionStorage.setItem("kyc", status);
                              this.$router.push("/kyc-application");
                              console.warn("Unknown KYC status:", status);
                          }
                        }).catch(() => {
                          this.$store.dispatch("triggerSnackbar", "Failed to check KYC status. Please try again.");
                        });
                      }


                      if (sessionStorage.getItem("userRole") == "MAKER") {

                        // Fetch KYC details + user details concurrently
                        const [kycDetails, userInfo] = await Promise.all([
                          this.statusDetails({ address: this.zetrixAccount }),
                          this.userDetails(this.zetrixAccount)
                        ]);

                        if (userInfo?.object?.name) {
                          sessionStorage.setItem("userName", userInfo.object.name);

                        }
                        if (kycDetails?.object?.user?.name) {
                          sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
                        }

                        sessionStorage.setItem("kyc", "ACTIVE");

                        this.$router.push("/certificate");



                      }

                      if (sessionStorage.getItem("userRole") == "ADMIN") {

                        // Fetch KYC details + user details concurrently
                        const [kycDetails, userInfo] = await Promise.all([
                          this.statusDetails({ address: this.zetrixAccount }),
                          this.userDetails(this.zetrixAccount)
                        ]);

                        if (userInfo?.object?.name) {
                          sessionStorage.setItem("userName", userInfo.object.name);

                        }
                        if (kycDetails?.object?.user?.name) {
                          sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
                        }

                        sessionStorage.setItem("kyc", null);

                        this.$router.push("/issuer/application-list");



                      }

                    } else {
                      this.$store.dispatch("triggerSnackbar", "Authentication failed. Please try again.");
                    }
                  }).catch(() => {
                    this.$store.dispatch("triggerSnackbar", "Authentication failed. Please try again.");
                  });
                }
              }
            );
          }).catch(() => {
            this.$store.dispatch("triggerSnackbar", "Failed to get authentication data. Please try again.");
          });

        } else {
          switch (resp.code) {
            case 1:
              alert(ZETRIX_RESPONSE_MESSAGE.CANCEL);
              break;
            case -1:
              alert(ZETRIX_RESPONSE_MESSAGE.FAIL);
              break;
            case 100001:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100001);
              break;
            case 100002:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100002);
              break;
            case 100003:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100003);
              break;
            case 100004:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100004);
              break;
          }
        }
      });
    },

    isMainNetwork() {

      const chainId = this.$store.state.chainId;
      // chainId is only populated by the browser-extension path (getChainId callback).
      // Mobile/QR connections never set it, so treat empty chainId as "not yet known"
      // and rely solely on the URL-based environment check (BT-2213 port from DID).
      if (!chainId) {
        this.setHeaderMessage({ show: false, message: "" });
        return true;
      }
      const host = `${window.location.protocol}//${window.location.host}`;
      const url = process.env.VUE_APP_API_BASE_URL;
      const isNonProd = _isNonProdUrl(host) || _isNonProdUrl(url);
      if (isNonProd && chainId === mainChainId) {
        this.setHeaderMessage({
          show: true,
          message: testChainIdMessage,
        });
        this.errorNetwork = testChainIdMessage;
        return false;
      } else if (!isNonProd && chainId !== mainChainId) {
        this.setHeaderMessage({
          show: true,
          message: chainIdMessage,
        });
        this.errorNetwork = chainIdMessage;
        return false;
      }
      this.setHeaderMessage({
        show: false,
        message: "",
      });
      return true;
    },

    // --- Mobile three-tap state machine ---
    // State flow: idle → connecting → ready → waiting → sign-ready → signing → idle

    async prepareMobileConnect() {
      if (this.mobileStep !== "idle") return;
      this.mobileStep = "connecting";
      try { this.zetrixWalletConnect?.closeConnect(); } catch { }
      this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(false));
      let connectTimerId;
      const connectTimeout = new Promise((_, reject) => {
        connectTimerId = setTimeout(() => reject(new Error("timeout")), 15000);
      });
      try {
        await Promise.race([this.zetrixWalletConnect.connect(), connectTimeout]);
        clearTimeout(connectTimerId);
        this.mobileStep = "ready";
      } catch (err) {
        clearTimeout(connectTimerId);
        this.mobileStep = "idle";
        this.$store.dispatch("triggerSnackbar", "Could not connect to wallet service. Please try again.");
      }
    },

    openWalletApp() {
      if (this.mobileStep !== "ready") return;
      if (!this.isMainNetwork()) {
        this.$store.dispatch("triggerSnackbar", this.errorNetwork);
        this.mobileStep = "idle";
        return;
      }
      this.mobileStep = "waiting";
      this._authTimeout = setTimeout(() => {
        if (this.mobileStep === "waiting") this.cancelMobileConnect();
      }, 90000);

      // Safety net: reconnect only if socket is definitively CLOSED/CLOSING.
      // zetrixWalletConnect is a Vue reactive Proxy, so walletSocket._websocket
      // (an SDK internal) is always undefined through the Proxy. Treating undefined
      // as "not open" and firing connect() kills the existing OPEN socket and causes
      // auth() to fail ("wallet connection lost"). Skip reconnect when _ws is undefined
      // — trust that prepareMobileConnect() already established the connection.
      const _ws = this.zetrixWalletConnect?.walletSocket?._websocket;
      if (_ws && (_ws.readyState === WebSocket.CLOSED || _ws.readyState === WebSocket.CLOSING)) {
        this.zetrixWalletConnect.connect().catch(() => { });
      }

      // auth() fires the zetrix:// deep link synchronously — gesture context preserved.
      const authPromise = this.zetrixWalletConnect.auth();
      authPromise
        .then(async (res) => {
          clearTimeout(this._authTimeout);
          this._authTimeout = null;
          if (!res || res.code !== 0) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Authorization rejected. Please try again.");
            return;
          }
          const address = res.data.address;
          const blob = await this.authBlob(address);
          if (!blob) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Failed to get authentication blob. Please try again.");
            return;
          }
          // iOS drops the WebSocket during the app switch to the wallet.
          // Reconnect with a fresh instance to re-register the session.
          try {
            await this.afterOpenToBind();
          } catch {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Failed to re-establish wallet session. Please try again.");
            return;
          }
          this.pendingSign = { address, blob };
          this.mobileStep = "sign-ready";
        })
        .catch(() => {
          this.cancelMobileConnect();
          this.$store.dispatch("triggerSnackbar", "Wallet connection lost. Please try again.");
        });
    },

    async afterOpenToBind() {
      // Close the old socket first — without closeConnect() the old session stays alive
      // server-side and sign responses route to it instead of the fresh connection.
      try { this.zetrixWalletConnect?.closeConnect(); } catch { }
      const fresh = new ZetrixWalletConnect(_walletConnectOpts(false));
      await fresh.connect();
      this.zetrixWalletConnect = fresh;
    },

    confirmSign() {
      if (this.mobileStep !== "sign-ready" || !this.pendingSign) return;
      this.mobileStep = "signing";
      this._signTimeout = setTimeout(() => {
        if (this.mobileStep === "signing") this.cancelMobileConnect();
      }, 90000);

      const { address, blob } = this.pendingSign;
      // signMessage() fires the zetrix:// deep link synchronously — gesture context preserved.
      const signPromise = this.zetrixWalletConnect.signMessage({ message: blob });
      signPromise
        .then(async (signResult) => {
          clearTimeout(this._signTimeout);
          this._signTimeout = null;
          if (!signResult || signResult.code !== 0) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Sign request rejected. Please try again.");
            return;
          }
          const accessToken = await this.authLogin(
            address,
            signResult.data.signData,
            signResult.data.publicKey
          );
          if (!accessToken) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Authentication failed. Please try again.");
            return;
          }
          sessionStorage.setItem("zetrixAccount", address);
          sessionStorage.setItem("publicKey", signResult.data.publicKey);
          window.dispatchEvent(new Event("account-connected"));
          this.zetrixAccount = address;
          this.isLogin = "true";
          sessionStorage.setItem("isLogin", this.isLogin);
          this.pendingSign = null;
          this.mobileStep = "idle";
          await this.handleKycByRole();
        })
        .catch(() => {
          this.cancelMobileConnect();
          this.$store.dispatch("triggerSnackbar", "Sign failed. Please try again.");
        });
    },

    cancelMobileConnect() {
      clearTimeout(this._authTimeout);
      clearTimeout(this._signTimeout);
      this._authTimeout = null;
      this._signTimeout = null;
      try { this.zetrixWalletConnect?.closeConnect(); } catch { }
      try { this.zetrixWalletConnect?.disconnect(); } catch { }
      this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(false));
      this.pendingSign = null;
      this.mobileStep = "idle";
    },

    getAccountBalance(zetrixAccount) {
      this.$store
        .dispatch("wallet/getAccountBalance", { zetrixAccount: zetrixAccount })
        .then((response) => {
          if (response != null) {
            this.setBalance(response.balance);
            this.balance = response.balance;
          } else {
            this.setBalance(0);
            this.balance = 0;
          }
        });
    },

    signBlob(blob) {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (typeof this.ztx !== "undefined") {
            this.ztx.signMessage({ message: blob }, function (result) {
              if (result.code === 0) {
                return resolve(result);
              }
              return resolve(false);
            });
          } else {
            return resolve(false);
          }
        }, 0);
      });
    },

    signBlobMobileZtx(blob) {
      return new Promise((resolve) => {
        setTimeout(() => {
          this.zetrixWalletConnect
            .signBlob({ message: blob })
            .then((res) => {
              if (res.code === 0) {
                return resolve(res);
              } else {
                return resolve(false);
              }
            })
            .catch((e) => {
              return resolve(false);
            });
        }, 0);
      });
    },

    disconnectWalletZtx() {
      clearTimeout(this._authTimeout);
      clearTimeout(this._signTimeout);
      try { this.zetrixWalletConnect?.closeConnect(); } catch { }
      try { this.zetrixWalletConnect?.disconnect(); } catch { }
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/";
    },

    authBlob(address) {
      return this.$store.dispatch("issuer/authBlob", { address }).then((blob) => {
        return blob || null;
      });
    },

    authLogin(address, signMessage, publicKey) {
      return this.$store
        .dispatch("issuer/authLogin", { address, signMessage, publicKey })
        .then((accessToken) => {
          return accessToken || null;
        });
    },

    statusKyc(data) {
      return this.$store.dispatch("issuer/statusKyc", data).then((status) => {
        return status || null; // status can be null or PENDING/ACTIVE/etc.
      });
    },

    async userDetails(data) {
      return this.$store.dispatch("users/userDetail", data).then(async (res) => {
        return res || null;
      });
    },


    async statusDetails(data) {
      return this.$store.dispatch("issuer/kycDetails", data).then(async (res) => {
        return res || null;
      });
    },

    templateBlob(data) {
      return this.$store.dispatch("issuer/templateBlob", { data }).then((blob) => {
        return blob || null;
      });
    },

    async handleKycByRole() {
      const role = sessionStorage.getItem("userRole");

      if (role === "ISSUER") {
        const status = await this.statusKyc();
        if (!status) {
          sessionStorage.setItem("kyc", status);
          this.$router.push("/kyc-application");
          return;
        }

        switch (status) {
          case "PENDING":
          case "REJECTED":
          case "REVOKED":
            sessionStorage.setItem("kyc", status);
            this.$router.push("/kyc-status");
            break;
          case "ACTIVE": {
            const [kycDetails, userInfo] = await Promise.all([
              this.statusDetails({ address: this.zetrixAccount }),
              this.userDetails(this.zetrixAccount),
            ]);

            sessionStorage.setItem("kyc", status);

            if (userInfo?.object?.name) {
              sessionStorage.setItem("userName", userInfo.object.name);
            }
            if (kycDetails?.object?.user?.name) {
              sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
            }

            this.$router.push("/certificate");
            break;
          }
          default:
            sessionStorage.setItem("kyc", status);
            this.$router.push("/kyc-application");
            console.warn("Unknown KYC status:", status);
        }
      }

      if (role === "MAKER") {
        const [kycDetails, userInfo] = await Promise.all([
          this.statusDetails({ address: this.zetrixAccount }),
          this.userDetails(this.zetrixAccount),
        ]);

        if (userInfo?.object?.name) {
          sessionStorage.setItem("userName", userInfo.object.name);
        }
        if (kycDetails?.object?.user?.name) {
          sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
        }

        sessionStorage.setItem("kyc", "ACTIVE");
        this.$router.push("/certificate");
      }

      if (role === "ADMIN") {
        const [kycDetails, userInfo] = await Promise.all([
          this.statusDetails({ address: this.zetrixAccount }),
          this.userDetails(this.zetrixAccount),
        ]);

        if (userInfo?.object?.name) {
          sessionStorage.setItem("userName", userInfo.object.name);
        }
        if (kycDetails?.object?.user?.name) {
          sessionStorage.setItem("companyName", kycDetails?.object?.user?.name);
        }

        sessionStorage.setItem("kyc", null);
        this.$router.push("/issuer/application-list");
      }
    },

    async connectWalletQrCode() {
      if (!this.isMainNetwork()) {
        this.$store.dispatch("triggerSnackbar", this.errorNetwork);
        return;
      }
      try {
        await this.zetrixWalletConnect.connect();
        const res = await this.zetrixWalletConnect.auth();
        if (!res || res.code !== 0) {
          try { this.zetrixWalletConnect?.closeConnect(); } catch { }
          this.$store.dispatch("triggerSnackbar", "Authorization cancelled. Please try again.");
          return;
        }
        const address = res.data.address;

        const blob = await this.authBlob(address);
        if (!blob) {
          try { this.zetrixWalletConnect?.closeConnect(); } catch { }
          this.$store.dispatch("triggerSnackbar", "Failed to get authentication data. Please try again.");
          return;
        }

        const signResult = await this.zetrixWalletConnect.signMessage({ message: blob });
        if (!signResult || signResult.code !== 0) {
          try { this.zetrixWalletConnect?.closeConnect(); } catch { }
          this.$store.dispatch("triggerSnackbar", "Sign request rejected. Please try again.");
          return;
        }

        const accessToken = await this.authLogin(address, signResult.data.signData, signResult.data.publicKey);
        if (!accessToken) {
          try { this.zetrixWalletConnect?.closeConnect(); } catch { }
          this.$store.dispatch("triggerSnackbar", "Authentication failed. Please try again.");
          return;
        }

        sessionStorage.setItem("zetrixAccount", address);
        sessionStorage.setItem("publicKey", signResult.data.publicKey);
        window.dispatchEvent(new Event("account-connected"));
        this.zetrixAccount = address;
        this.isLogin = "true";
        sessionStorage.setItem("isLogin", this.isLogin);
        await this.handleKycByRole();
      } catch (err) {
        console.error("QR code wallet connection failed:", err);
        this.$store.dispatch("triggerSnackbar", "QR wallet connection failed. Please try again.");
      }
    },


  },
};
